import React from 'react';
import { Button, Modal } from 'react-bootstrap';

// ConfirmModal — the single shared confirmation dialog for consequential actions (Close, Delete,
// etc.) across the app: size="lg", backdrop="static", footer No (light) / {confirmLabel} (success).
// Parameterised by title/body/confirmLabel so every call site reuses this one component instead of
// hand-rolling its own near-identical modal. `danger` is the *trigger* elsewhere (e.g. the Delete
// dropdown item), never the confirm button here.
//
// **Return answers it**, because that is what Return does to a question and this dialog asks one —
// it has no field of its own that could want the key.
//
// By FOCUSING the confirm as the dialog opens, rather than by handling the key. Three reasons, the
// last of which is decisive:
//
//  - a focus ring shows which answer Return will give, where a hidden handler would not;
//  - Space, and every other way a button is pressed, comes along for free;
//  - the key cannot be handled here anyway. `onKeyDown` on a react-bootstrap `<Modal>` lands on
//    `.modal-dialog` (Modal.js spreads the rest of its props onto the inner Dialog), while the
//    thing that HOLDS focus is the `.modal` container wrapping it. Events travel up, so a keydown
//    on the container never reaches a listener on its own child — it silently does nothing.
//
// `onEntered`, not `autoFocus`: the modal takes focus for itself when it opens, after its children
// have mounted, so a child's own autoFocus is given and then taken away.
//
// What this cannot do is confirm behind somebody's back. Focus is moved once, onto a button; a
// person who tabs to No gets No, and Escape still declines. The same arrangement the cell-delete
// dialog uses (missionControl/surfaces/cells/deleteCellDialog.js), which mirrors this component
// deliberately — the two are one family and answer the keyboard the same way.
export default function ConfirmModal({ show, onHide, onConfirm, title, body, confirmLabel = "Yes" }) {
  const confirmRef = React.useRef(null);

  const handleConfirm = () => {
    // Dispatch the action
    onConfirm();

    // Close modal
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static"
      onEntered={() => confirmRef.current && confirmRef.current.focus()}>
      <Modal.Header closeButton>
        <Modal.Title>
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {body}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>No</Button>
        <Button ref={confirmRef} variant="success" onClick={handleConfirm}>{confirmLabel}</Button>
      </Modal.Footer>
    </Modal>
  );
}
